/**
 * Share crediting — securities, the ready-to-credit awards, the batches built
 * from them and the registrar / depository correspondence a batch goes through.
 */

import { TOUR_AWARDS } from './tour-sample-awards';
import { TOUR_EMPLOYEES } from './tour-sample-people';

export interface TourSecurity {
  id: number;
  /**
   * What the Share Crediting security pickers show. They read `companyName`
   * (`getOptionLabel={option => option.companyName}`) — without it every
   * option renders as a blank row and a tour cannot pick a security at all.
   */
  companyName: string;
  label: string;
  name: string;
  securityCode: string;
  exchange: string;
  currency: string;
  issuedShares: number;
  paidUpCapital: number;
}

export const TOUR_SECURITIES: readonly TourSecurity[] = Object.freeze([
  {
    id: 1,
    companyName: 'Northwind Holdings Ordinary Shares',
    label: 'Northwind Holdings Ordinary Shares',
    name: 'Northwind Holdings Ordinary Shares',
    securityCode: 'NWH-ORD',
    exchange: 'SGX',
    currency: 'SGD',
    issuedShares: 480000000,
    paidUpCapital: 48000000,
  },
  {
    id: 2,
    companyName: 'Northwind Holdings Preference Shares',
    label: 'Northwind Holdings Preference Shares',
    name: 'Northwind Holdings Preference Shares',
    securityCode: 'NWH-PREF',
    exchange: 'SGX',
    currency: 'SGD',
    issuedShares: 25000000,
    paidUpCapital: 25000000,
  },
]);

export const TOUR_PRIMARY_SECURITY = TOUR_SECURITIES[0];

export interface TourSignatory {
  id: number;
  name: string;
  /**
   * The value the batch wizard's "Verified By" / "Approved By" selects use —
   * they map the options to `signatoryPurpose` and resolve the saved id back
   * through it, so a signatory without one cannot be displayed or selected.
   */
  signatoryPurpose: string;
  designation: string;
  email: string;
}

export const TOUR_SIGNATORIES: readonly TourSignatory[] = Object.freeze([
  {
    id: 1,
    name: 'Sarah Lim',
    signatoryPurpose: 'Company Secretary',
    designation: 'Company Secretary',
    email: 'sarah.lim@demo.sharesvision.example',
  },
  {
    id: 2,
    name: 'Rajesh Menon',
    signatoryPurpose: 'Operations Director',
    designation: 'Operations Director',
    email: 'rajesh.menon@demo.sharesvision.example',
  },
  {
    id: 3,
    name: 'Mei Ling Chua',
    signatoryPurpose: 'Compliance Officer',
    designation: 'Compliance Officer',
    email: 'meiling.chua@demo.sharesvision.example',
  },
]);

/**
 * A vested award waiting to be pulled into a crediting batch — a row of the
 * Create Batch tab.
 *
 * That table is per **award**, not per employee: it counts the participants
 * behind the award (with a CDP account, without one, resigned before vesting)
 * and the shares that go with them. Those are the column ids it renders, and
 * the "# of Participants" cell calls `.toLocaleString()` on the value, so a
 * row missing them throws rather than showing a blank.
 */
export interface TourReadyAward {
  id: string;
  awardNo: string;
  mainAwardNo: string;
  awardDate: string;
  vestingDate: string;
  tranche: number;
  employeeId: string;
  employeeName: string;
  planName: string;
  noParticipantWithCDP: number;
  noParticipantWithoutCDP: number;
  noParticipantResigned: number;
  noOfShareWithCDP: number;
  noOfShareWithoutCDP: number;
  noOfShareExcludedCDP: number;
  totalShares: number;
  totalParticipants: number;
  sharesToCredit: number;
  depositoryAgent: string;
  accountNumber: string;
  status: string;
}

const readyAward = (
  award: (typeof TOUR_AWARDS)[number],
  index: number
): TourReadyAward => {
  const tranche = award.vestingSchedule[0];
  const withCDP = 2 + (index % 2);
  const withoutCDP = 1;
  const sharesWithCDP = Math.round(
    (tranche.sharesVested * withCDP) / (withCDP + withoutCDP)
  );

  return {
    id: `${award.awardNo}-ready-${index}`,
    awardNo: award.awardNo,
    mainAwardNo: award.awardNo,
    awardDate: award.awardDate,
    vestingDate: tranche.vestingDate,
    tranche: tranche.sNo,
    employeeId: award.employeeId,
    employeeName: award.employeeName,
    planName: award.planName,
    noParticipantWithCDP: withCDP,
    noParticipantWithoutCDP: withoutCDP,
    noParticipantResigned: index % 2,
    noOfShareWithCDP: sharesWithCDP,
    noOfShareWithoutCDP: tranche.sharesVested - sharesWithCDP,
    noOfShareExcludedCDP: 0,
    totalShares: tranche.sharesVested,
    totalParticipants: withCDP + withoutCDP,
    sharesToCredit: tranche.sharesVested,
    depositoryAgent: 'Central Depository (Pte) Limited',
    accountNumber: `168-000-${String(123456 + index)}`,
    status: 'Ready to Credit',
  };
};

/**
 * The awards the Create Batch tab offers. The batches below were generated
 * from the rest of the sample awards, so nothing here is already batched.
 */
export const TOUR_READY_AWARDS: readonly TourReadyAward[] = Object.freeze(
  TOUR_AWARDS.slice(0, 3).map((award, index) => readyAward(award, index))
);

/** The awards already pulled into the generated batches — one set, no award
 * appearing in two batches. */
const TOUR_BATCHED_AWARDS: readonly TourReadyAward[] = Object.freeze(
  TOUR_AWARDS.slice(3).map((award, index) => readyAward(award, index + 3))
);

/** Sum a slice of batched awards into the totals a batch row carries. */
const batchTotals = (awards: readonly TourReadyAward[]) => ({
  awardNos: awards.map(award => award.awardNo),
  totalShares: awards.reduce((sum, award) => sum + award.totalShares, 0),
  totalParticipants: awards.reduce(
    (sum, award) => sum + award.totalParticipants,
    0
  ),
});

export const TOUR_TOTAL_TREASURY_SHARES = 1250000;

/**
 * A generated crediting batch, in the shape the Process Batch and Crediting
 * Details lists actually read.
 *
 * Both lists render `BatchResponseItem` columns, and the two tables disagree
 * on two of them — Process Batch reads `statusName` / `createdByName` while
 * Crediting Details reads `batchStatus` / `createdBy` — so every row carries
 * both spellings. `statusId` values are the real batch statuses
 * (`batchStatusData`, 1500–1510): the wizard maps them to its stage and the
 * list uses them to decide between the editable flow and the read-only
 * preview, so inventing ids here would open a tour on the wrong screen.
 */
export interface TourCreditingBatch {
  id: number;
  batchId: number;
  /** Row id, batch number and route parameter are the same value. */
  batchNo: number;
  batchNoBySecurity: number;
  batchName: string;
  batchReference: string;
  securityId: number;
  securityName: string;
  companyName: string;
  shareTypeName: string;
  batchDate: string;
  vestingDate: string;
  creditingDate: string | null;
  awardNos: string[];
  totalShares: number;
  totalParticipants: number;
  noOfRecipients: number;
  wgtAvgTreasuryPrice: number;
  statusId: number;
  statusName: string;
  /** Crediting Details' spelling of `statusName`. */
  batchStatus: string;
  workflowStatus: string;
  createdById: number;
  createdByName: string;
  /** Crediting Details' spelling of `createdByName`. */
  createdBy: string;
  createdAt: string;
  createdDate: string;
  submittedByName: string;
  submittedDate: string | null;
  verifiedByName: string;
  verifiedDate: string | null;
  approvedByName: string;
  approvedDate: string | null;
  submitted: boolean;
  listingDate: string | null;
  listingPrice: number | null;
}

export const TOUR_CREDITING_BATCHES: readonly TourCreditingBatch[] =
  Object.freeze([
    {
      id: 1201,
      batchId: 1201,
      batchNo: 1201,
      batchNoBySecurity: 1,
      batchName: 'PSP 2025 Tranche 1 Crediting',
      batchReference: 'BATCH-TOUR-1201',
      securityId: 1,
      securityName: 'Northwind Holdings Ordinary Shares',
      companyName: 'Northwind Holdings Ordinary Shares',
      shareTypeName: 'Treasury Shares',
      batchDate: '2026-03-16',
      vestingDate: '2026-03-15',
      creditingDate: null,
      ...batchTotals(TOUR_BATCHED_AWARDS.slice(0, 2)),
      noOfRecipients: batchTotals(TOUR_BATCHED_AWARDS.slice(0, 2))
        .totalParticipants,
      wgtAvgTreasuryPrice: 4.6215,
      // Pending Batch Processing — opens the wizard on its first stage, which
      // is what the "Process a Crediting Batch" tour walks through.
      statusId: 1500,
      statusName: 'Pending Batch Processing',
      batchStatus: 'Pending Batch Processing',
      workflowStatus: 'Pending Batch Processing',
      createdById: 9001,
      createdByName: 'Sarah Lim',
      createdBy: 'Sarah Lim',
      createdAt: '2026-03-16T09:30:00Z',
      createdDate: '2026-03-16T09:30:00Z',
      submittedByName: '',
      submittedDate: null,
      verifiedByName: '',
      verifiedDate: null,
      approvedByName: '',
      approvedDate: null,
      submitted: false,
      listingDate: null,
      listingPrice: null,
    },
    {
      id: 1202,
      batchId: 1202,
      batchNo: 1202,
      batchNoBySecurity: 2,
      batchName: 'RSP 2025 Tranche 1 Crediting',
      batchReference: 'BATCH-TOUR-1202',
      securityId: 1,
      securityName: 'Northwind Holdings Ordinary Shares',
      companyName: 'Northwind Holdings Ordinary Shares',
      shareTypeName: 'Treasury Shares',
      batchDate: '2026-03-17',
      vestingDate: '2026-03-15',
      creditingDate: null,
      ...batchTotals(TOUR_BATCHED_AWARDS.slice(2, 3)),
      noOfRecipients: batchTotals(TOUR_BATCHED_AWARDS.slice(2, 3))
        .totalParticipants,
      wgtAvgTreasuryPrice: 4.588,
      // Batch Verified — the wizard opens on Account Validation, so the list
      // shows a batch mid-flow alongside the one still to be processed.
      statusId: 1503,
      statusName: 'Batch Verified',
      batchStatus: 'Batch Verified',
      workflowStatus: 'Batch Verified',
      createdById: 9002,
      createdByName: 'Arun Kumar',
      createdBy: 'Arun Kumar',
      createdAt: '2026-03-17T11:10:00Z',
      createdDate: '2026-03-17T11:10:00Z',
      submittedByName: 'Arun Kumar',
      submittedDate: '2026-03-17T12:00:00Z',
      verifiedByName: 'Sarah Lim',
      verifiedDate: '2026-03-18T09:00:00Z',
      approvedByName: '',
      approvedDate: null,
      submitted: true,
      listingDate: null,
      listingPrice: null,
    },
    {
      id: 1203,
      batchId: 1203,
      batchNo: 1203,
      batchNoBySecurity: 3,
      batchName: 'ESOS 2024 Exercise Crediting',
      batchReference: 'BATCH-TOUR-1203',
      securityId: 1,
      securityName: 'Northwind Holdings Ordinary Shares',
      companyName: 'Northwind Holdings Ordinary Shares',
      shareTypeName: 'New Shares',
      batchDate: '2026-02-03',
      vestingDate: '2026-02-01',
      creditingDate: '2026-02-10',
      ...batchTotals(TOUR_BATCHED_AWARDS.slice(3, 4)),
      noOfRecipients: batchTotals(TOUR_BATCHED_AWARDS.slice(3, 4))
        .totalParticipants,
      wgtAvgTreasuryPrice: 4.82,
      // Credited — the finished batch the "Review Credit Details" tour reads.
      statusId: 1510,
      statusName: 'Credited',
      batchStatus: 'Credited',
      workflowStatus: 'Credited',
      createdById: 9003,
      createdByName: 'Daniel Tan',
      createdBy: 'Daniel Tan',
      createdAt: '2026-02-03T15:45:00Z',
      createdDate: '2026-02-03T15:45:00Z',
      submittedByName: 'Daniel Tan',
      submittedDate: '2026-02-04T10:00:00Z',
      verifiedByName: 'Sarah Lim',
      verifiedDate: '2026-02-05T10:00:00Z',
      approvedByName: 'Rajesh Menon',
      approvedDate: '2026-02-06T10:00:00Z',
      submitted: true,
      listingDate: '2026-02-09',
      listingPrice: 4.82,
    },
    {
      id: 1204,
      batchId: 1204,
      batchNo: 1204,
      batchNoBySecurity: 4,
      batchName: 'PSP 2025 Tranche 2 Crediting',
      batchReference: 'BATCH-TOUR-1204',
      securityId: 1,
      securityName: 'Northwind Holdings Ordinary Shares',
      companyName: 'Northwind Holdings Ordinary Shares',
      shareTypeName: 'Treasury Shares',
      batchDate: '2026-03-19',
      vestingDate: '2026-03-15',
      creditingDate: null,
      ...batchTotals(TOUR_BATCHED_AWARDS.slice(4, 5)),
      noOfRecipients: batchTotals(TOUR_BATCHED_AWARDS.slice(4, 5))
        .totalParticipants,
      wgtAvgTreasuryPrice: 4.605,
      // Pending Batch Verification — the checker's queue. The "Approve a
      // Crediting Batch" tour needs a batch sitting here, and the list opens
      // it read-only, as an approval screen should.
      statusId: 1501,
      statusName: 'Pending Batch Verification',
      batchStatus: 'Pending Batch Verification',
      workflowStatus: 'Pending Batch Verification',
      createdById: 9002,
      createdByName: 'Arun Kumar',
      createdBy: 'Arun Kumar',
      createdAt: '2026-03-19T08:20:00Z',
      createdDate: '2026-03-19T08:20:00Z',
      submittedByName: 'Arun Kumar',
      submittedDate: '2026-03-19T09:05:00Z',
      verifiedByName: '',
      verifiedDate: null,
      approvedByName: '',
      approvedDate: null,
      submitted: true,
      listingDate: null,
      listingPrice: null,
    },
  ]);

export const TOUR_PRIMARY_BATCH = TOUR_CREDITING_BATCHES[0];

/** The batches sitting in the checker's approval queue. */
export const TOUR_CHECKER_BATCH_STATUS_ID = 1501;

/** One recipient line inside a crediting batch. */
export interface TourBatchRecipient {
  id: number;
  sNo: number;
  employeeId: string;
  employeeName: string;
  awardNo: string;
  planName: string;
  vestingDate: string;
  sharesCredited: number;
  depositoryAgent: string;
  accountNumber: string;
  status: string;
}

/** Built from the batched awards, so a batch's recipients are its own. */
export const TOUR_BATCH_RECIPIENTS: readonly TourBatchRecipient[] =
  Object.freeze(
    TOUR_BATCHED_AWARDS.map((award, index) => ({
      id: 3300 + index,
      sNo: index + 1,
      employeeId: award.employeeId,
      employeeName: award.employeeName,
      awardNo: award.awardNo,
      planName: award.planName,
      vestingDate: award.vestingDate,
      sharesCredited: award.sharesToCredit,
      depositoryAgent: award.depositoryAgent,
      accountNumber: award.accountNumber,
      status: 'Credited',
    }))
  );

/**
 * A tranche row of the batch — what the wizard's first stage tabulates.
 * `useBatchDetailsData` maps `batchDetails.awards` into the Tranche Details
 * table, so these field names are the ones that table reads.
 */
export interface TourBatchAward {
  awardNo: string;
  tranche: number;
  vestingDate: string;
  fairValue: number;
  totalShares: number;
  totalParticipants: number;
}

/** The letters a batch carries into the validation stages. */
export const TOUR_BATCH_LETTERS = Object.freeze([
  {
    templateId: 4101,
    templateName: 'Crediting Advice',
    templateCode: 'CREDIT-ADVICE',
    isSystemTemplate: true,
  },
  {
    templateId: 4102,
    templateName: 'Depository Instruction',
    templateCode: 'DEPOSITORY-INSTRUCTION',
    isSystemTemplate: true,
  },
]);

/**
 * The detail payload behind a batch — the record the wizard reads on
 * `/tenant/batch-details/:batchNo`.
 *
 * It spreads the list row (so the header, status and totals agree with the row
 * the user clicked) and adds what only the detail screen shows: the tranche
 * table, the signatories its selects bind to, the letters and the saved
 * stakeholder notifications.
 */
export const getTourBatchDetail = (batchNo: string | number) => {
  const batch =
    TOUR_CREDITING_BATCHES.find(
      item => String(item.batchNo) === String(batchNo)
    ) ?? TOUR_PRIMARY_BATCH;

  const awards: TourBatchAward[] = batch.awardNos.map((awardNo, index) => ({
    awardNo,
    tranche: index + 1,
    vestingDate: batch.vestingDate,
    fairValue: Number((4.5 + index * 0.12).toFixed(4)),
    totalShares: Math.round(batch.totalShares / batch.awardNos.length),
    totalParticipants: Math.max(
      1,
      Math.round(batch.totalParticipants / batch.awardNos.length)
    ),
  }));

  return {
    ...batch,
    security: TOUR_PRIMARY_SECURITY,
    awards,
    letters: TOUR_BATCH_LETTERS,
    // Bound by id to the signatory master served at `/master/signatories`; the
    // wizard joins on the id and displays the master's `signatoryPurpose`.
    verifiedSignatoryId: TOUR_SIGNATORIES[0].id,
    verifiedSignatoryName: TOUR_SIGNATORIES[0].name,
    verifiedSignatory: TOUR_SIGNATORIES[0],
    verifiedBySignatory: TOUR_SIGNATORIES[0],
    approvedSignatories: [TOUR_SIGNATORIES[1], TOUR_SIGNATORIES[2]],
    approvedBySignatories: [TOUR_SIGNATORIES[1], TOUR_SIGNATORIES[2]],
    stakeholderNotifications: TOUR_STAKEHOLDER_NOTIFICATIONS,
    latestIssuedShares: TOUR_PRIMARY_SECURITY.issuedShares,
    latestPaidupCapital: TOUR_PRIMARY_SECURITY.paidUpCapital,
    items: TOUR_BATCH_RECIPIENTS,
    recipients: TOUR_BATCH_RECIPIENTS,
    totalCount: TOUR_BATCH_RECIPIENTS.length,
  };
};

/** Registrar / depository correspondence tracked against a batch. */
export const TOUR_REGISTRAR_SUBMISSIONS = Object.freeze([
  {
    id: 4401,
    templateId: 4103,
    templateName: 'Share Registrar Instruction',
    sentTo: 'registrar@demo.sharesvision.example',
    sentBy: 'Sarah Lim',
    sentAt: '2026-03-18T10:00:00Z',
    status: 'Sent',
  },
]);

export const TOUR_REGISTRAR_VERIFICATIONS = Object.freeze([
  {
    id: 4501,
    verifiedBy: 'Northwind Share Registrar',
    verifiedAt: '2026-03-19T14:30:00Z',
    status: 'Verified',
    remarks: 'Allotment schedule reconciled.',
  },
]);

export const TOUR_STAKEHOLDER_NOTIFICATIONS = Object.freeze([
  {
    id: 4601,
    stakeholder: 'Company Secretary',
    email: 'sarah.lim@demo.sharesvision.example',
    templateId: 4103,
    templateName: 'Share Registrar Instruction',
    status: 'Sent',
    sentAt: '2026-03-20T09:15:00Z',
  },
  {
    id: 4602,
    stakeholder: 'Finance',
    email: 'finance@demo.sharesvision.example',
    templateId: 4103,
    templateName: 'Share Registrar Instruction',
    status: 'Pending',
    sentAt: null,
  },
]);

/**
 * The Crediting Details list (Review Credit Details tour).
 *
 * This tab and the page behind it list **batches**, not per-employee lines —
 * both render `BatchResponseItem` columns — so the rows are the crediting
 * batches themselves. The detail page then filters them down to the batch
 * numbers ticked on the tab, which is why the ids here are the batch numbers.
 */
export type TourCreditingDetail = TourCreditingBatch;

/** Batch Verified (1503) onwards — a batch still awaiting verification has no
 * crediting detail to read. */
export const TOUR_CREDITING_DETAILS_FROM_STATUS_ID = 1503;

export const TOUR_CREDITING_DETAILS: readonly TourCreditingDetail[] =
  Object.freeze(
    TOUR_CREDITING_BATCHES.filter(
      batch => batch.statusId >= TOUR_CREDITING_DETAILS_FROM_STATUS_ID
    )
  );

/** Stock prices offered when setting a batch's listing price. */
export const TOUR_STOCK_PRICES = Object.freeze([
  { listingDate: '2026-03-20', closingPrice: 4.82, currency: 'SGD' },
  { listingDate: '2026-03-19', closingPrice: 4.78, currency: 'SGD' },
  { listingDate: '2026-03-18', closingPrice: 4.85, currency: 'SGD' },
]);

/** The emails a security's stakeholder notifications default to. */
export const TOUR_COMBINED_SECURITY_EMAILS = {
  securityId: TOUR_PRIMARY_SECURITY.id,
  emails: TOUR_EMPLOYEES.slice(0, 3).map(employee => employee.email),
};
